import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { SiWhatsapp } from 'react-icons/si';
import { IMAGES } from '@/assets/images';
import { SERVICES, WHY_CHOOSE_US, TESTIMONIALS, GALLERY_ITEMS, STATS, CLIENTS } from '@/data/index';
import { fadeInUp, staggerContainer, staggerItem, slideInRight } from '@/lib/motion';
import { ServiceCard, StatCard, TestimonialCard, GalleryCard, WhyUsCard } from '@/components/Cards';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useState } from 'react';
export default function Home() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    serviceType: '',
    eventDate: '',
    quantity: '',
    message: ''
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const whatsappMessage = `مرحباً، أرغب في طلب عرض سعر:\n\nالاسم: ${formData.name}\nالشركة: ${formData.company}\nنوع الخدمة: ${formData.serviceType}\nتاريخ الفعالية: ${formData.eventDate}\nالكمية: ${formData.quantity}\nالرسالة: ${formData.message}`;
    const whatsappUrl = `https://wa.me/966550096297?text=${encodeURIComponent(whatsappMessage)}`;
    window.open(whatsappUrl, '_blank');
  };
  return <div className="w-full" dir="rtl">
        <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img src={IMAGES.CORPORATE_CATERING_7} alt="أطياف الخبازين" className="w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
          </div>

          <div className="container mx-auto px-4 relative z-10 text-center">
            <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="max-w-4xl mx-auto">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-foreground mb-6" style={{
            fontFamily: 'Cairo, Playfair Display, serif'
          }}>
                نقدم خدمات الضيافة التي تليق بفعالياتك
              </h1>
              <div className="w-32 h-1 bg-accent mx-auto mb-8" />
              <p className="text-xl md:text-2xl text-muted-foreground mb-12 leading-relaxed">
                معمل متخصص في الكيترنج، الكوفي بريك، المخبوزات، والحلويات للشركات والمناسبات والكافيهات
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 text-lg" asChild>
                  <a href="#quote">طلب عرض سعر</a>
                </Button>
                <Button size="lg" variant="outline" className="border-accent text-accent hover:bg-accent hover:text-accent-foreground px-8 py-6 text-lg" asChild>
                  <a href="https://wa.me/966550096297" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                    <SiWhatsapp className="w-5 h-5" />
                    تواصل واتساب
                  </a>
                </Button>
              </div>
            </motion.div>

            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 1
        }} className="absolute bottom-12 left-1/2 -translate-x-1/2">
              <a href="#about" className="flex flex-col items-center gap-2 text-muted-foreground hover:text-accent transition-colors">
                <span className="text-sm">اكتشف المزيد</span>
                <ChevronDown className="w-6 h-6 animate-bounce" />
              </a>
            </motion.div>
          </div>
        </section>

        <section className="py-12 bg-muted">
          <div className="container mx-auto px-4">
            <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {STATS.map(stat => <motion.div key={stat.labelAr} variants={staggerItem}>
                  <StatCard stat={stat} />
                </motion.div>)}
            </motion.div>
          </div>
        </section>

        <section id="about" className="py-20 lg:py-32 bg-background">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <motion.div initial={{
            opacity: 0,
            x: -40
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }}>
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                  من نحن
                </h2>
                <div className="w-24 h-1 bg-accent mb-8" />
                <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                  أطياف الخبازين معمل سعودي متخصص في صناعة الضيافة والمخبوزات الفاخرة. نقدم حلول متكاملة للشركات والمناسبات والكافيهات بأعلى معايير الجودة والاحترافية.
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  نؤمن أن التميز يكمن في التفاصيل، لذلك نحرص على تقديم منتجات بطعم ثابت ومظهر أنيق وتجربة متكاملة تعكس الاحترافية والفخامة. كما نواصل تطوير قدراتنا وخطوط الإنتاج بشكل مستمر لتلبية احتياجات الشركات والمناسبات والكافيهات بأعلى معايير الجودة والدقة والالتزام.
                </p>
              </motion.div>

              <motion.div initial={{
            opacity: 0,
            x: 40
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} className="relative">
                <img src="/images/20251012_151808.jpg" alt="من نحن" className="w-full h-[500px] object-cover rounded-lg shadow-lg" />
              </motion.div>
            </div>
          </div>
        </section>

        <section id="services" className="py-20 lg:py-32 bg-muted">
          <div className="container mx-auto px-4">
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                خدماتنا
              </h2>
              <div className="w-24 h-1 bg-accent mx-auto mb-6" />
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                نقدم مجموعة متكاملة من الخدمات لتلبية احتياجاتكم
              </p>
            </motion.div>

            <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {SERVICES.map(service => <motion.div key={service.id} variants={staggerItem}>
                  <ServiceCard service={service} />
                </motion.div>)}
            </motion.div>
          </div>
        </section>

        <section id="gallery" className="py-20 lg:py-32 bg-background">
          <div className="container mx-auto px-4">
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                معرض الأعمال
              </h2>
              <div className="w-24 h-1 bg-accent mx-auto mb-6" />
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                نماذج من أعمالنا المتميزة
              </p>
            </motion.div>

            <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {GALLERY_ITEMS.map(item => (
                <GalleryCard key={item.id} item={item} />
              ))}
            </motion.div>
          </div>
        </section>

        <section id="why-us" className="py-20 lg:py-32 bg-muted">
          <div className="container mx-auto px-4">
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                لماذا تختارنا
              </h2>
              <div className="w-24 h-1 bg-accent mx-auto mb-6" />
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                نتميز بمعايير عالية تضمن رضاكم التام
              </p>
            </motion.div>

            <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {WHY_CHOOSE_US.map(item => <motion.div key={item.id} variants={staggerItem}>
                  <WhyUsCard item={item} />
                </motion.div>)}
            </motion.div>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">آراء العملا</h2>
              <div className="w-24 h-1 bg-accent mx-auto mb-6" />
            </motion.div>

            <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {TESTIMONIALS.map(testimonial => <motion.div key={testimonial.id} variants={staggerItem}>
                  <TestimonialCard testimonial={testimonial} />
                </motion.div>)}
            </motion.div>
          </div>
        </section>

        <section id="clients" className="py-20 bg-muted">
          <div className="container mx-auto px-4">
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                عملاؤنا
              </h2>
              <div className="w-24 h-1 bg-accent mx-auto mb-6" />
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                نفخر بثقة كبرى الشركات والمؤسسات السعودية
              </p>
            </motion.div>

            <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {CLIENTS.map(client => <motion.div key={client.id} variants={staggerItem} className="bg-card rounded-lg p-6 flex items-center justify-center text-center shadow-sm hover:shadow-md transition-shadow">
                  <span className="text-muted-foreground font-medium">{client.nameAr}</span>
                </motion.div>)}
            </motion.div>
          </div>
        </section>

        <section id="quote" className="py-20 lg:py-32 bg-background">
          <div className="container mx-auto px-4">
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                  اطلب عرض سعر
                </h2>
                <div className="w-24 h-1 bg-accent mx-auto mb-6" />
                <p className="text-lg text-muted-foreground">
                  املأ النموذج وسنتواصل معك في أقرب وقت
                </p>
              </div>

              <form onSubmit={handleSubmit} className="bg-card rounded-lg p-8 shadow-lg space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-foreground">الاسم</Label>
                    <Input id="name" placeholder="أدخل اسمك" value={formData.name} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} required className="bg-background border-border" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-foreground">الشركة</Label>
                    <Input id="company" placeholder="اسم الشركة" value={formData.company} onChange={e => setFormData({
                  ...formData,
                  company: e.target.value
                })} className="bg-background border-border" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="serviceType" className="text-foreground">نوع الخدمة</Label>
                  <Select value={formData.serviceType || 'none'} onValueChange={value => setFormData({
                ...formData,
                serviceType: value === 'none' ? '' : value
              })}>
                    <SelectTrigger className="bg-background border-border">
                      <SelectValue placeholder="اختر نوع الخدمة" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">اختر نوع الخدمة</SelectItem>
                      <SelectItem value="corporate">كيترنج الشركات</SelectItem>
                      <SelectItem value="coffee-break">كوفي بريك</SelectItem>
                      <SelectItem value="bakery">مخبوزات يومية</SelectItem>
                      <SelectItem value="desserts">حلويات للكافيهات</SelectItem>
                      <SelectItem value="weddings">أعراس ومناسبات</SelectItem>
                      <SelectItem value="boxes">بوكسات ضيافة</SelectItem>
                      <SelectItem value="other">أخرى</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="eventDate" className="text-foreground">تاريخ الفعالية</Label>
                    <Input id="eventDate" type="date" value={formData.eventDate} onChange={e => setFormData({
                  ...formData,
                  eventDate: e.target.value
                })} className="bg-background border-border" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="quantity" className="text-foreground">الكمية المتوقعة</Label>
                    <Input id="quantity" placeholder="عدد الأشخاص" value={formData.quantity} onChange={e => setFormData({
                  ...formData,
                  quantity: e.target.value
                })} className="bg-background border-border" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-foreground">رسالتك</Label>
                  <Textarea id="message" placeholder="أخبرنا المزيد عن احتياجاتك" value={formData.message} onChange={e => setFormData({
                ...formData,
                message: e.target.value
              })} rows={5} className="bg-background border-border" />
                </div>

                <Button type="submit" size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                  إرسال الطلب عبر واتساب
                </Button>
              </form>
            </motion.div>
          </div>
        </section>

        <section className="py-12 bg-muted">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-2">الهاتف</h3>
                <p className="text-muted-foreground" dir="ltr">+966550096297</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-2">البريد الإلكتروني</h3>
                <p className="text-muted-foreground">info@atiaf.co</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-2">الموقع</h3>
                <p className="text-muted-foreground">الرياض، المملكة العربية السعودية</p>
              </div>
            </div>
          </div>
        </section>
      </div>;
}